// src/components/stats/CatalogHealthPanel.js
//
// Katalog sağlığı. Uç Google Play'in tüm ürün kataloğunu listelediği için
// 5-15 saniye sürer; bu yüzden:
//   • diğer bloklarla birlikte değil, kendi sekmesinde ve tembel yüklenir
//   • otomatik periyodik tazeleme YOK, yalnızca elle "Yeniden çalıştır"
//
// `playError` doluysa Play'e ulaşılamamıştır: `play` ve `bridges` boş gelir ama
// `migration` DB'den hesaplandığı için yine doğrudur — o blok çizilmeye devam eder.

import React, { useState, useEffect, useCallback } from 'react';
import styles from './statsBlocks.module.css';
import LoadingSpinner from '../common/LoadingSpinner';
import {
    fetchCatalogHealth,
    readStatsError,
    formatCount,
    formatDateTime,
    daysSince,
} from '../../services/statsApi';

// Kılavuz §5'teki uyarı türleri: ne demek + operatör ne yapmalı.
const ISSUE_GUIDE = {
    slots_exhausted: { title: 'Play ürün limiti doldu', action: 'Satılamaz eski ürünleri silin.' },
    slots_low: { title: 'Boş slot azaldı', action: 'Temizlik planlayın.' },
    bundle_missing_in_play: { title: "Play'de ürün yok", action: "Bundle'ı yeniden yayınlayın." },
    plan_open_but_no_option: { title: "Satışta ama Play'de option yok", action: 'Fiyatı düşürün ya da Google Play satışını kapatın.' },
    plan_open_but_option_inactive: { title: 'Option aktif değil', action: 'Yeniden yayınlayın.' },
    bundle_dark: { title: 'Ülke tamamen kapalı', action: 'Acil: satılabilir tek option yok, yeniden yayınlayın.' },
    unpublished_and_no_product: { title: 'Hiç yayınlanmamış', action: "Bundle'ı yayınlayın." },
};

// Köprü ürünü silmek için bundle tarafının hazır olması yetmez: Google yeni
// ürünü cihazlara yayana kadar eski ürünü silmek, güncellemeyi almamış
// kullanıcıları o ülkede satın alamaz hale getirir.
const BRIDGE_WAIT_DAYS = 3;

const Meter = ({ value, max, tone }) => {
    const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
    return (
        <div className={styles.meter} role="presentation">
            <div className={`${styles.meterFill} ${tone ? styles[tone] : ''}`} style={{ width: `${pct}%` }} />
        </div>
    );
};

const CatalogHealthPanel = () => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            setData(await fetchCatalogHealth());
        } catch (err) {
            console.error('Katalog sağlığı alınamadı:', err);
            setError(readStatsError(err, 'Katalog sağlığı alınamadı.').message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Sekme açıldığında bir kez çalışır; sonrası elle.
    useEffect(() => { load(); }, [load]);

    if (isLoading) {
        return (
            <section className={styles.block}>
                <header className={styles.blockHeader}>
                    <h2 className={styles.blockTitle}>Katalog sağlığı</h2>
                </header>
                <div className={styles.lazyLoading}>
                    <LoadingSpinner />
                    <p className={styles.muted}>
                        Google Play kataloğu okunuyor — bu kontrol 5-15 saniye sürer.
                    </p>
                </div>
            </section>
        );
    }

    if (error && !data) {
        return (
            <section className={styles.block}>
                <header className={styles.blockHeader}>
                    <h2 className={styles.blockTitle}>Katalog sağlığı</h2>
                </header>
                <p className={styles.errorText}>{error}</p>
                <button type="button" className="ghost" onClick={load}>Tekrar dene</button>
            </section>
        );
    }

    if (!data) return null;

    const play = data.play || {};
    const migration = data.migration || {};
    const issues = data.issues || [];
    const bridges = data.bridges || [];
    const criticalCount = issues.filter((i) => i.severity === 'critical').length;

    return (
        <div className={styles.stack}>
            <section className={styles.block}>
                <header className={styles.blockHeader}>
                    <h2 className={styles.blockTitle}>Katalog sağlığı</h2>
                    <span className={styles.blockMeta}>{formatDateTime(data.generatedAt)}</span>
                    <button type="button" className="ghost" onClick={load}>Yeniden çalıştır</button>
                </header>

                {data.playError && (
                    <div className={`${styles.alert} ${styles.alertWarn}`}>
                        Play durumu okunamadı: {data.playError}. Aşağıdaki geçiş oranı veritabanından
                        hesaplandığı için doğrudur; slot ve köprü bilgileri bu çalıştırmada eksik.
                    </div>
                )}

                <div className={styles.twoCol}>
                    {!data.playError && (
                        <div>
                            <h3 className={styles.subTitle}>Google Play slotları</h3>
                            <div className={styles.meterRow}>
                                <span className={`${styles.meterValue} num`}>
                                    {formatCount(play.totalProducts)} / {formatCount(play.slotLimit)}
                                </span>
                                <span className={styles.factHint}>
                                    {formatCount(play.slotsRemaining)} boş slot
                                </span>
                            </div>
                            <Meter
                                value={play.totalProducts}
                                max={play.slotLimit}
                                tone={play.slotsRemaining === 0 ? 'meterDanger' : play.slotsRemaining < 25 ? 'meterWarn' : undefined}
                            />
                            <p className={styles.meterLegend}>
                                <span className="num">{formatCount(play.bundleProducts)}</span> yeni bundle ürünü ·{' '}
                                <span className="num">{formatCount(play.prodProducts)}</span> eski prod ürünü
                                {play.unsellableProdProducts > 0 && (
                                    <> · <span className="num">{formatCount(play.unsellableProdProducts)}</span> satılamaz (silinirse slot kazancı)</>
                                )}
                            </p>
                        </div>
                    )}

                    <div>
                        <h3 className={styles.subTitle}>Yeni şemaya geçiş</h3>
                        <div className={styles.meterRow}>
                            <span className={`${styles.meterValue} num`}>%{formatCount(migration.percentComplete)}</span>
                            <span className={styles.factHint}>
                                {formatCount(migration.remainingSellable)} satılabilir kalem bekliyor
                            </span>
                        </div>
                        <Meter value={migration.percentComplete} max={100} />
                        <p className={styles.meterLegend}>
                            Standart <span className="num">{formatCount(migration.standardPublished)}/{formatCount(migration.standardBundles)}</span>
                            {' · '}
                            Sınırsız <span className="num">{formatCount(migration.unlimitedPublished)}/{formatCount(migration.unlimitedBundles)}</span>
                        </p>
                    </div>
                </div>
            </section>

            <section className={styles.block}>
                <header className={styles.blockHeader}>
                    <h2 className={styles.blockTitle}>
                        Uyarılar
                        <span className={styles.countBadge}>{issues.length}</span>
                    </h2>
                    {criticalCount > 0 && (
                        <span className={styles.blockMeta}>{criticalCount} kritik</span>
                    )}
                </header>

                {issues.length === 0 ? (
                    <p className={styles.muted}>Katalogda uyarı yok.</p>
                ) : (
                    <ul className={styles.issueList}>
                        {/* Dizi kritikler üstte olacak şekilde sıralı gelir; olduğu gibi çiziyoruz. */}
                        {issues.map((issue, i) => {
                            const guide = ISSUE_GUIDE[issue.kind] || {};
                            return (
                                <li
                                    key={`${issue.kind}-${issue.bundleId || ''}-${issue.planId || ''}-${i}`}
                                    className={`${styles.issue} ${issue.severity === 'critical' ? styles.issueCritical : styles.issueWarning}`}
                                >
                                    <div className={styles.issueHead}>
                                        <span className={styles.issueTitle}>{guide.title || issue.kind}</span>
                                        {issue.bundleTitle && (
                                            <span className={styles.issueTarget}>
                                                {issue.bundleTitle}
                                                {issue.planTitle ? ` · ${issue.planTitle}` : ''}
                                                {issue.bundleId ? <span className="num"> (#{issue.bundleId})</span> : null}
                                            </span>
                                        )}
                                    </div>
                                    {issue.detail && <p className={styles.issueDetail}>{issue.detail}</p>}
                                    {guide.action && <p className={styles.issueAction}>{guide.action}</p>}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            {!data.playError && (
                <section className={styles.block}>
                    <header className={styles.blockHeader}>
                        <h2 className={styles.blockTitle}>
                            Köprü ürünler
                            <span className={styles.countBadge}>{bridges.length}</span>
                        </h2>
                    </header>
                    <p className={styles.blockIntro}>
                        Hem yeni <code>bundle_</code> hem eski <code>prod_</code> ürünü Play'de duran ülkeler.
                        Geçiş döneminde bu kasıtlıdır — eski uygulama sürümleri hâlâ <code>prod_</code>'dan satın alır.
                        Her eski ürün bir slot yediği için yayılım oturunca silinmeli. Silme işlemi bu ekranda yok.
                    </p>

                    {bridges.length === 0 ? (
                        <p className={styles.muted}>Temizlenecek köprü ürün yok.</p>
                    ) : (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Bundle</th>
                                    <th>Yayın tarihi</th>
                                    <th className={styles.numCol}>Eski ürün</th>
                                    <th>Durum</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bridges.map((bridge) => {
                                    const age = daysSince(bridge.publishedAt);
                                    const tooFresh = age !== null && age < BRIDGE_WAIT_DAYS;
                                    return (
                                        <tr key={bridge.bundleId}>
                                            <td>
                                                {bridge.title} <span className="num">#{bridge.bundleId}</span>
                                            </td>
                                            <td className={styles.nowrap}>
                                                {formatDateTime(bridge.publishedAt)}
                                                {age !== null && <span className={styles.factHint}> {age} gün önce</span>}
                                            </td>
                                            <td className={`${styles.numCol} num`} title={(bridge.leftoverProdIds || []).join(', ')}>
                                                {formatCount(bridge.leftoverProdCount)}
                                            </td>
                                            <td>
                                                {!bridge.safeToDelete ? (
                                                    <span className={styles.tagMuted}>bundle hazır değil</span>
                                                ) : tooFresh ? (
                                                    <span className={styles.tagWarn}>bekle · yayın çok yeni</span>
                                                ) : (
                                                    <span className={styles.tagOk}>silinebilir</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </section>
            )}
        </div>
    );
};

export default CatalogHealthPanel;
